import apiFetch from "@/app/auth/apiFetch";
import type { NavigateFunction } from "react-router-dom";

const API_URL = import.meta.env.VITE_API_URL;

// --------------------------------------------------------------------------- //
// Caps globaux par tier (table quota_tier_config)
// --------------------------------------------------------------------------- //
// "rolling" = par abonnement (anti-détection) ; "account_rolling" = par compte
// LinkedIn (anti-ban scraping) ; "monthly" = business par organisation ;
// "dispatch" = weighted scrape dispatch (mix de job_types worker, tier "global").
export type QuotaDimension = "rolling" | "monthly" | "account_rolling" | "dispatch";

export interface QuotaConfigEntry {
  tier: string;
  bucket: string;
  dimension: QuotaDimension;
  daily: number | null;
  hourly: number | null;
  monthly: number | null;
  // Relative dispatch weight (dimension "dispatch" only, null otherwise).
  weight: number | null;
  updated_at?: string | null;
  updated_by?: string | null;
}

export interface QuotaConfigResponse {
  entries: QuotaConfigEntry[];
  enforcement_enabled: boolean;
  // Enforcement du quota par compte LinkedIn (back WORKER_ACCOUNT_QUOTA_ENABLED).
  worker_account_quota_enabled?: boolean;
  // Weighted scrape dispatch flag (back WORKER_WEIGHTED_DISPATCH_ENABLED).
  worker_weighted_dispatch_enabled?: boolean;
}

// Payload d'écriture (sous-ensemble : pas de updated_at/by).
export interface QuotaConfigEntryInput {
  tier: string;
  bucket: string;
  dimension: QuotaDimension;
  daily?: number | null;
  hourly?: number | null;
  monthly?: number | null;
  weight?: number | null;
}

export async function getQuotaConfig(
  navigate: NavigateFunction
): Promise<QuotaConfigResponse> {
  const res = await apiFetch(`${API_URL}/admin/quota-config`, { method: "GET" }, navigate);
  if (!res?.ok) throw new Error("Erreur lors du chargement de la configuration des quotas");
  return res.json();
}

export async function updateQuotaConfig(
  entries: QuotaConfigEntryInput[],
  navigate: NavigateFunction
): Promise<{ updated: number; enforcement_enabled: boolean }> {
  const res = await apiFetch(
    `${API_URL}/admin/quota-config`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entries }),
    },
    navigate
  );
  if (!res?.ok) {
    const detail = await res?.json?.().catch(() => null);
    throw new Error(detail?.detail ?? "Erreur lors de la mise à jour des quotas");
  }
  return res.json();
}

// --------------------------------------------------------------------------- //
// Quotas mensuels par organisation
// --------------------------------------------------------------------------- //
export interface OrgQuotaMetric {
  quota_monthly: number;
  used_month: number;
}

export interface OrgQuotaSnapshot {
  organization_id: number;
  name: string;
  tier: string;
  enforcement_enabled: boolean;
  quota_overridden: boolean;
  usage_period_start: string | null;
  metrics: {
    invites: OrgQuotaMetric;
    messages: OrgQuotaMetric;
    enrichments: OrgQuotaMetric;
  };
}

export async function getOrgQuotas(
  organizationId: number,
  navigate: NavigateFunction
): Promise<OrgQuotaSnapshot> {
  const res = await apiFetch(
    `${API_URL}/admin/organizations/${organizationId}/quotas`,
    { method: "GET" },
    navigate
  );
  if (!res?.ok) throw new Error("Erreur lors du chargement des quotas de l'organisation");
  return res.json();
}

export interface OrgQuotaUpdateInput {
  invites_quota_monthly?: number;
  messages_quota_monthly?: number;
  enrichments_quota_monthly?: number;
}

export async function updateOrgQuotas(
  organizationId: number,
  payload: OrgQuotaUpdateInput,
  navigate: NavigateFunction
): Promise<OrgQuotaSnapshot> {
  const res = await apiFetch(
    `${API_URL}/admin/organizations/${organizationId}/quotas`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    },
    navigate
  );
  if (!res?.ok) {
    const detail = await res?.json?.().catch(() => null);
    throw new Error(detail?.detail ?? "Erreur lors de la modification des quotas");
  }
  return res.json();
}

export async function resetOrgQuotas(
  organizationId: number,
  navigate: NavigateFunction
): Promise<{ flushed_accounts: number; flushed_keys: number }> {
  const res = await apiFetch(
    `${API_URL}/admin/organizations/${organizationId}/quotas/reset`,
    { method: "POST" },
    navigate
  );
  if (!res?.ok) throw new Error("Erreur lors du reset des quotas de l'organisation");
  return res.json();
}

export async function resetUserQuotas(
  userId: string,
  buckets: string[] | null,
  navigate: NavigateFunction
): Promise<{ flushed_keys: number }> {
  const qs = buckets && buckets.length ? `?buckets=${encodeURIComponent(buckets.join(","))}` : "";
  const res = await apiFetch(
    `${API_URL}/admin/users/${userId}/quotas/reset${qs}`,
    { method: "POST" },
    navigate
  );
  if (!res?.ok) throw new Error("Erreur lors du reset des quotas du compte");
  return res.json();
}

/**
 * Flush des compteurs glissants Redis du quota par COMPTE LinkedIn
 * (`quota:li:{publicIdentifier}:{bucket}:{h|d}`). Déblocage ponctuel d'un compte.
 * Back: POST /api/admin/linkedin-accounts/{public_identifier}/quotas/reset.
 */
export async function resetLinkedinAccountQuotas(
  publicIdentifier: string,
  buckets: string[] | null,
  navigate: NavigateFunction
): Promise<{ public_identifier: string; flushed_keys: number; buckets: string[] }> {
  const qs = buckets && buckets.length ? `?buckets=${encodeURIComponent(buckets.join(","))}` : "";
  const res = await apiFetch(
    `${API_URL}/admin/linkedin-accounts/${encodeURIComponent(publicIdentifier)}/quotas/reset${qs}`,
    { method: "POST" },
    navigate
  );
  if (!res?.ok) {
    const detail = await res?.json?.().catch(() => null);
    throw new Error(detail?.detail ?? "Erreur lors du reset des quotas du compte LinkedIn");
  }
  return res.json();
}
